import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart3, Loader2, Star, X, Calendar, Search,
  Table as TableIcon, Layers, CalendarDays, ChevronUp, ChevronDown,
  Eye, EyeOff, Trash2,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { CARD, CARD_BASE, FilterButton, DatePanel, ActiveChip } from '../components/ui.jsx'

/**
 * CST scores — admin view across every operation.
 *
 * Three view modes (default = table, group-by-op, group-by-date) on top of
 * shared filters (date range, max mean, comment-only) and sort controls.
 *
 * Data sources:
 *   /api/cst/responses    one row per CST submission (table / group-by-date)
 *   /api/cst              one row per operation (group-by-op)
 * The fetch picks the right endpoint per view mode.
 */
const VIEWS = [
  { key: 'table',  label: 'ทุก response', icon: TableIcon    },
  { key: 'op',     label: 'รวมตาม op',    icon: Layers       },
  { key: 'date',   label: 'รวมตามวัน',    icon: CalendarDays },
]

export default function CstScores() {
  const [view, setView] = useState('table')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo,   setDateTo]   = useState('')
  const [search,   setSearch]   = useState('')
  const [sortKey,  setSortKey]  = useState('date')   // 'date' | 'mean' | 'company' | 'count'
  const [sortDir,  setSortDir]  = useState('desc')

  function clearAll() { setDateFrom(''); setDateTo(''); setSearch('') }

  return (
    <div className="px-4 py-5 md:px-10 md:py-8 max-w-[1320px] mx-auto">
      <header className="mb-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-amber-700/80 font-semibold">Operation analytics</div>
          <h1 className="text-[22px] font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <BarChart3 size={22} className="text-amber-500" /> CST scores
          </h1>
          <p className="text-[12.5px] text-zinc-500 mt-0.5">รวมคะแนนความพึงพอใจจากทุก Operation</p>
        </div>
      </header>

      {/* View switcher */}
      <div className="mb-3 inline-flex rounded-lg bg-zinc-100 p-1">
        {VIEWS.map((v) => {
          const Icon = v.icon
          const on = view === v.key
          return (
            <button key={v.key} onClick={() => setView(v.key)}
              className={
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium transition-colors ' +
                (on ? 'bg-white shadow-sm text-zinc-900 ring-1 ring-zinc-200' : 'text-zinc-600 hover:text-zinc-900')
              }>
              <Icon size={13} /> {v.label}
            </button>
          )
        })}
      </div>

      {/* Filter toolbar */}
      <Toolbar
        dateFrom={dateFrom}     setDateFrom={setDateFrom}
        dateTo={dateTo}         setDateTo={setDateTo}
        search={search}         setSearch={setSearch}
        sortKey={sortKey}       setSortKey={setSortKey}
        sortDir={sortDir}       setSortDir={setSortDir}
        view={view}
        onClearAll={clearAll}
      />

      {view === 'op'    && <OpView    dateFrom={dateFrom} dateTo={dateTo} search={search} sortKey={sortKey} sortDir={sortDir} />}
      {view === 'date'  && <DateView  dateFrom={dateFrom} dateTo={dateTo} search={search} sortKey={sortKey} sortDir={sortDir} />}
      {view === 'table' && <TableView dateFrom={dateFrom} dateTo={dateTo} search={search} sortKey={sortKey} sortDir={sortDir} />}
    </div>
  )
}


/* ──────────────────────────────── Toolbar ──────────────────────────────── */

function Toolbar({ dateFrom, setDateFrom, dateTo, setDateTo, search, setSearch, sortKey, setSortKey, sortDir, setSortDir, view, onClearAll }) {
  // Sort keys depend on view.
  const sortOptions = view === 'op'
    ? [{ key: 'mean', label: 'คะแนนเฉลี่ย' }, { key: 'count', label: 'จำนวน response' }, { key: 'date', label: 'วันที่' }, { key: 'company', label: 'บริษัท' }]
    : view === 'date'
      ? [{ key: 'date', label: 'วันที่' }, { key: 'mean', label: 'คะแนนเฉลี่ย' }, { key: 'count', label: 'จำนวน response' }]
      : [{ key: 'date', label: 'วันที่' }, { key: 'mean', label: 'คะแนนเฉลี่ย' }, { key: 'company', label: 'บริษัท' }]

  const activeFilters = ((dateFrom || dateTo) ? 1 : 0) + (search.trim() ? 1 : 0)

  return (
    <div className={`${CARD_BASE} relative mb-4`}>
      <div className="px-3 py-2.5 flex flex-col gap-2 md:flex-row md:items-center md:flex-wrap">
        <FilterButton icon={Calendar} label="Date" count={(dateFrom || dateTo) ? 1 : 0}>
          <DatePanel from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t) }} direction="past" />
        </FilterButton>

        <div className="relative md:min-w-[240px] flex-1 max-w-[360px]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} maxLength={100}
            placeholder="ค้นหาบริษัท…"
            className="w-full pl-7 pr-7 py-1.5 rounded-md ring-1 ring-zinc-200 focus:ring-2 focus:ring-amber-400 focus:outline-none text-[12.5px] text-zinc-700 placeholder:text-zinc-400" />
          {search && (
            <button type="button" onClick={() => setSearch('')}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 inline-flex items-center justify-center rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100">
              <X size={11} />
            </button>
          )}
        </div>

        <div className="ml-auto inline-flex items-center gap-1">
          <span className="text-[11px] text-zinc-500 mr-1">เรียงตาม</span>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value)}
            className="px-2 py-1.5 rounded-md ring-1 ring-zinc-200 hover:bg-zinc-50 text-[12.5px] text-zinc-700 focus:ring-2 focus:ring-amber-400 focus:outline-none">
            {sortOptions.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <button onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')}
            title={sortDir === 'asc' ? 'น้อย → มาก' : 'มาก → น้อย'}
            className="w-8 h-8 inline-flex items-center justify-center rounded-md ring-1 ring-zinc-200 hover:bg-zinc-50 text-zinc-600">
            {sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      </div>

      {activeFilters > 0 && (
        <div className="px-3 pb-2.5 flex items-center gap-1.5 flex-wrap">
          {(dateFrom || dateTo) && <ActiveChip icon={Calendar} label="Date" value={`${dateFrom || '…'} → ${dateTo || '…'}`} onRemove={() => { setDateFrom(''); setDateTo('') }} />}
          {search.trim() && <ActiveChip icon={Search} label="" value={`"${search.trim()}"`} onRemove={() => setSearch('')} />}
          <button onClick={onClearAll}
            className="ml-auto text-[12px] text-zinc-500 hover:text-rose-600 inline-flex items-center gap-1">
            <X size={11} /> Clear all
          </button>
        </div>
      )}
    </div>
  )
}

/* ──────────────────────────── Group-by-op view ─────────────────────────── */

function OpView({ dateFrom, dateTo, search, sortKey, sortDir }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [lastViewedOpId, setLastViewedOpId] = useState(() => {
    const v = parseInt(sessionStorage.getItem('lastViewedOpId') || '', 10)
    return Number.isFinite(v) ? v : null
  })

  useEffect(() => {
    let cancelled = false
    setRows(null); setError(null)
    api.cst.list({ date_from: dateFrom, date_to: dateTo })
      .then((r) => { if (!cancelled) setRows(r) })
      .catch((e) => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [dateFrom, dateTo])

  const sorted = useMemo(() => {
    if (!rows) return null
    const q = search.trim().toLowerCase()
    const filtered = q ? rows.filter((r) => (r.company_name || '').toLowerCase().includes(q)) : rows
    const cmp = (a, b) => {
      let av, bv
      if (sortKey === 'company') { av = a.company_name || ''; bv = b.company_name || '' }
      else if (sortKey === 'date') { av = a.start_date || ''; bv = b.start_date || '' }
      else if (sortKey === 'count') { av = a.n; bv = b.n }
      else { av = a.mean; bv = b.mean }
      if (av === bv) return 0
      return (av > bv ? 1 : -1) * (sortDir === 'asc' ? 1 : -1)
    }
    return [...filtered].sort(cmp)
  }, [rows, search, sortKey, sortDir])

  if (error) return <Banner tone="rose">{error}</Banner>
  return (
    <div className={CARD}>
      <table className="w-full table-fixed text-[13px]">
        <colgroup><col className="w-[36%]" /><col className="hidden md:table-column" /><col /><col /><col /></colgroup>
        <thead className="bg-zinc-50/70 text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
          <tr>
            <th className="px-3 md:px-5 py-3 text-left">Company</th>
            <th className="hidden md:table-cell px-5 py-3 text-left">Date</th>
            <th className="px-3 md:px-5 py-3 text-right">N responses</th>
            <th className="px-3 md:px-5 py-3 text-left">Mean</th>
            <th className="hidden md:table-cell px-5 py-3 text-left">Per question</th>
          </tr>
        </thead>
        <tbody>
          {!sorted && <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-400"><Loader2 className="inline w-4 h-4 animate-spin mr-1.5" />Loading…</td></tr>}
          {sorted && sorted.length === 0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-500">ยังไม่มีคะแนนตามตัวกรองนี้</td></tr>}
          {sorted && sorted.map((r) => {
            const isLastViewed = r.operation_id === lastViewedOpId
            return (
              <tr key={r.operation_id}
                  onClick={() => { sessionStorage.setItem('lastViewedOpId', String(r.operation_id)); setLastViewedOpId(r.operation_id); navigate(`/operations/${r.operation_id}/cst`) }}
                  className={'border-b border-zinc-100 last:border-b-0 transition-colors cursor-pointer ' +
                    (isLastViewed ? 'bg-amber-50 hover:bg-amber-100/70 ring-1 ring-inset ring-amber-300/40' : 'hover:bg-amber-50/40')}>
                <td className="px-3 md:px-5 py-2.5 align-top truncate" title={r.company_name}>
                  <div className="font-medium text-zinc-900 truncate">{r.company_name}</div>
                  <div className="md:hidden mt-0.5 text-[11.5px] text-zinc-500 tabular-nums">{fmtDate(r.start_date)}</div>
                </td>
                <td className="hidden md:table-cell px-5 py-2.5 text-zinc-700 tabular-nums align-top">{fmtDate(r.start_date)}</td>
                <td className="px-3 md:px-5 py-2.5 text-right text-zinc-700 tabular-nums align-top">{r.n}</td>
                <td className="px-3 md:px-5 py-2.5 align-top"><MeanBadge mean={r.mean} /></td>
                <td className="hidden md:table-cell px-5 py-2.5 align-top"><Mini means={[r.m1, r.m2, r.m3, r.m4, r.m5, r.m6]} /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}


/* ────────────────────────── Response-level data ────────────────────────── */

function useResponses({ dateFrom, dateTo }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    let cancelled = false
    setRows(null); setError(null)
    api.cst.responses({ date_from: dateFrom, date_to: dateTo })
      .then((r) => { if (!cancelled) setRows(r) })
      .catch((e) => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [dateFrom, dateTo, tick])
  return { rows, error, reload: () => setTick((t) => t + 1) }
}


/* ────────────────────────────── Table view ─────────────────────────────── */

function TableView({ dateFrom, dateTo, search, sortKey, sortDir }) {
  const navigate = useNavigate()
  const { rows, error, reload } = useResponses({ dateFrom, dateTo })

  const sorted = useMemo(() => {
    if (!rows) return null
    const q = search.trim().toLowerCase()
    const filtered = q ? rows.filter((r) => (r.company_name || '').toLowerCase().includes(q)) : rows
    const cmp = (a, b) => {
      let av, bv
      if (sortKey === 'company') { av = a.company_name || ''; bv = b.company_name || '' }
      else if (sortKey === 'date') { av = a.created_at; bv = b.created_at }
      else { av = a.mean; bv = b.mean }
      if (av === bv) return 0
      return (av > bv ? 1 : -1) * (sortDir === 'asc' ? 1 : -1)
    }
    return [...filtered].sort(cmp)
  }, [rows, search, sortKey, sortDir])

  if (error) return <Banner tone="rose">{error}</Banner>
  return (
    <div className={CARD}>
      <div className="px-4 py-2 border-b border-zinc-100 text-[11.5px] text-zinc-500 tabular-nums">
        {sorted ? <>{sorted.length.toLocaleString()} responses</> : 'Loading…'}
      </div>
      <table className="w-full text-[13px]">
        <thead className="bg-zinc-50/70 text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
          <tr>
            <th className="px-3 md:px-5 py-3 text-left">Date</th>
            <th className="px-3 md:px-5 py-3 text-left">Company</th>
            <th className="px-3 md:px-5 py-3 text-left">Mean</th>
            <th className="hidden md:table-cell px-5 py-3 text-left">Per question</th>
            <th className="px-3 md:px-5 py-3 text-left">Comment</th>
            <th className="px-3 md:px-5 py-3 text-right">จัดการ</th>
          </tr>
        </thead>
        <tbody>
          {!sorted && <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-400"><Loader2 className="inline w-4 h-4 animate-spin mr-1.5" />Loading…</td></tr>}
          {sorted && sorted.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-zinc-500">ไม่มี response ตามตัวกรอง</td></tr>}
          {sorted && sorted.map((r) => {
            const hidden = !!r.hidden
            return (
              <tr key={r.id}
                  onClick={() => navigate(`/operations/${r.operation_id}/cst`)}
                  className={'border-b border-zinc-100 last:border-b-0 cursor-pointer ' +
                    (hidden ? 'bg-zinc-50/80 hover:bg-zinc-100/70 opacity-70' : 'hover:bg-amber-50/40')}>
                <td className="px-3 md:px-5 py-2.5 align-top text-zinc-700 tabular-nums whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                <td className="px-3 md:px-5 py-2.5 align-top truncate font-medium text-zinc-900" title={r.company_name}>
                  {r.company_name}
                  {hidden && (
                    <span className="ml-1.5 align-middle text-[9.5px] px-1.5 py-0.5 rounded font-semibold bg-zinc-200 text-zinc-600 inline-flex items-center gap-0.5">
                      <EyeOff size={8} /> ซ่อน
                    </span>
                  )}
                </td>
                <td className="px-3 md:px-5 py-2.5 align-top"><MeanBadge mean={r.mean} /></td>
                <td className="hidden md:table-cell px-5 py-2.5 align-top"><Mini means={[r.q1, r.q2, r.q3, r.q4, r.q5, r.q6]} /></td>
                <td className={'px-3 md:px-5 py-2.5 align-top text-[12px] ' + (hidden ? 'text-zinc-400 line-through decoration-zinc-300' : 'text-zinc-600')}>
                  {r.suggestion ? <span className="line-clamp-2 whitespace-pre-wrap">{r.suggestion}</span> : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-3 md:px-5 py-2.5 align-top"><RowActions r={r} onChange={reload} /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}


/* ────────────────────────── Group-by-date view ─────────────────────────── */

function DateView({ dateFrom, dateTo, search, sortKey, sortDir }) {
  const { rows, error } = useResponses({ dateFrom, dateTo })

  const groups = useMemo(() => {
    if (!rows) return null
    const q = search.trim().toLowerCase()
    const filtered = q ? rows.filter((r) => (r.company_name || '').toLowerCase().includes(q)) : rows
    const map = new Map()
    for (const r of filtered) {
      const key = fmtDateKey(r.created_at)  // 'YYYY-MM-DD' Asia/Bangkok
      if (!map.has(key)) map.set(key, { key, items: [], sum: 0 })
      const g = map.get(key)
      g.items.push(r); g.sum += r.mean
    }
    const out = [...map.values()].map((g) => ({ ...g, mean: g.sum / g.items.length, count: g.items.length }))
    const cmp = (a, b) => {
      let av, bv
      if (sortKey === 'mean')  { av = a.mean;  bv = b.mean }
      else if (sortKey === 'count') { av = a.count; bv = b.count }
      else                     { av = a.key;   bv = b.key }
      if (av === bv) return 0
      return (av > bv ? 1 : -1) * (sortDir === 'asc' ? 1 : -1)
    }
    return out.sort(cmp)
  }, [rows, search, sortKey, sortDir])

  if (error) return <Banner tone="rose">{error}</Banner>
  if (!groups) return <Banner>กำลังโหลด…</Banner>
  if (!groups.length) return <Banner>ไม่มี response ตามตัวกรอง</Banner>

  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div key={g.key} className={`${CARD} p-4`}>
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <div className="text-[13px] font-semibold text-zinc-900 tabular-nums">{fmtDate(g.key)}</div>
              <div className="text-[11px] text-zinc-500">{g.count} response</div>
            </div>
            <MeanBadge mean={g.mean} />
          </div>
          <ul className="divide-y divide-zinc-100">
            {g.items.map((r) => (
              <ResponseRow key={r.id} r={r} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}


function ResponseRow({ r }) {
  const navigate = useNavigate()
  return (
    <li className="px-1 py-2 flex items-start gap-3 cursor-pointer hover:bg-zinc-50 rounded-md -mx-1"
        onClick={() => navigate(`/operations/${r.operation_id}/cst`)}>
      <span className="text-[10.5px] text-zinc-400 tabular-nums whitespace-nowrap pt-0.5">{fmtTime(r.created_at)}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium text-zinc-800 truncate">{r.company_name}</div>
        {r.suggestion && <p className="text-[11.5px] text-zinc-600 mt-0.5 whitespace-pre-wrap">{r.suggestion}</p>}
      </div>
      <MeanBadge mean={r.mean} />
    </li>
  )
}


/* ───────────────────────────── Row actions ─────────────────────────────── */

// Hide / delete controls for a single CST response, reused in the table view.
// Hide toggles the per-row flag (excludes the response from the HR report,
// still visible to staff here). Delete is a permanent, two-step removal.
// All clicks stopPropagation so the row's navigate handler doesn't fire.
function RowActions({ r, onChange }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const hidden = !!r.hidden

  async function toggleHide(e) {
    e.stopPropagation()
    setBusy(true); setErr(null)
    try {
      await api.operations.surveys.updateCstResponse(r.operation_id, r.id, { hidden: !hidden })
      onChange()
    } catch (ex) { setErr(ex?.message || 'error'); setBusy(false) }
  }

  async function remove(e) {
    e.stopPropagation()
    setBusy(true); setErr(null)
    try {
      await api.operations.surveys.deleteCstResponse(r.operation_id, r.id)
      onChange()
    } catch (ex) { setErr(ex?.message || 'error'); setBusy(false); setConfirming(false) }
  }

  return (
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      {err && <span className="text-[10.5px] text-rose-600 mr-1" title={err}>!</span>}
      {confirming ? (
        <>
          <button onClick={remove} disabled={busy}
            className="px-2 h-7 rounded-md text-[11px] font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 inline-flex items-center gap-1 whitespace-nowrap">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />} ลบถาวร
          </button>
          <button onClick={(e) => { e.stopPropagation(); setConfirming(false) }} disabled={busy}
            className="px-2 h-7 rounded-md text-[11px] text-zinc-600 ring-1 ring-zinc-200 hover:bg-zinc-100 disabled:opacity-50">
            ยกเลิก
          </button>
        </>
      ) : (
        <>
          <button onClick={toggleHide} disabled={busy} title={hidden ? 'แสดงในรายงาน HR' : 'ซ่อนจากรายงาน HR'}
            className={'w-7 h-7 rounded-md inline-flex items-center justify-center transition disabled:opacity-50 ' +
              (hidden
                ? 'text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50'
                : 'text-zinc-400 hover:text-amber-700 hover:bg-amber-50')}>
            {busy ? <Loader2 size={11} className="animate-spin" /> : hidden ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); setConfirming(true) }} disabled={busy} title="ลบ response นี้ถาวร"
            className="w-7 h-7 rounded-md inline-flex items-center justify-center text-zinc-400 hover:text-rose-700 hover:bg-rose-50 transition disabled:opacity-50">
            <Trash2 size={13} />
          </button>
        </>
      )}
    </div>
  )
}


/* ─────────────────────────────── Helpers ───────────────────────────────── */

function MeanBadge({ mean }) {
  const v = Number(mean || 0)
  const tone = v < 3 ? 'bg-rose-50 ring-rose-200 text-rose-800'
            : v < 4 ? 'bg-amber-50 ring-amber-200 text-amber-800'
            :         'bg-emerald-50 ring-emerald-200 text-emerald-800'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ring-1 text-[12.5px] font-semibold ${tone}`}>
      <Star size={11} fill="currentColor" /> {v.toFixed(2)}
    </span>
  )
}

function Mini({ means }) {
  const BAR_MAX = 22   // px — full-height bar for a perfect 5.00
  return (
    <div className="flex items-end gap-1">
      {means.map((m, i) => {
        const v = Number(m || 0)
        const h = Math.max(2, Math.round((v / 5) * BAR_MAX))
        const tone = v < 3 ? 'bg-rose-400' : v < 4 ? 'bg-amber-400' : 'bg-emerald-400'
        return (
          <div key={i} className="flex flex-col items-center gap-0.5" title={`Q${i+1}: ${v.toFixed(2)}`}>
            {/* Fixed-height bar slot so every column's bottom edge lines up;
                the colored bar grows upward inside it. */}
            <div className="w-3 flex items-end" style={{ height: BAR_MAX }}>
              <div className={`w-full rounded-sm ${tone}`} style={{ height: h }} />
            </div>
            <span className="text-[9.5px] text-zinc-400 tabular-nums">{v.toFixed(1)}</span>
          </div>
        )
      })}
    </div>
  )
}

function Banner({ tone, children }) {
  const cls = tone === 'rose'
    ? 'bg-rose-50 ring-rose-200 text-rose-700'
    : 'bg-zinc-50 ring-zinc-200 text-zinc-500'
  return <div className={`px-4 py-3 rounded-xl ring-1 ${cls} text-[13px]`}>{children}</div>
}

function fmtDate(key) {
  if (!key) return '—'
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtDateTime(unixSeconds) {
  if (!unixSeconds) return ''
  return new Date(unixSeconds * 1000).toLocaleString('th-TH-u-ca-gregory', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok',
  })
}
function fmtTime(unixSeconds) {
  if (!unixSeconds) return ''
  return new Date(unixSeconds * 1000).toLocaleTimeString('th-TH-u-ca-gregory', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok',
  })
}
function fmtDateKey(unixSeconds) {
  if (!unixSeconds) return ''
  // Compute YYYY-MM-DD in Asia/Bangkok regardless of local TZ.
  const parts = new Intl.DateTimeFormat('en-CA-u-ca-gregory', {
    timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(unixSeconds * 1000))
  const y = parts.find((p) => p.type === 'year')?.value
  const m = parts.find((p) => p.type === 'month')?.value
  const d = parts.find((p) => p.type === 'day')?.value
  return `${y}-${m}-${d}`
}
